import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import type { CustomReportOmsMonthlySummaryRouteModel } from 'donor-elf-web/routes/funds/fund/custom-reports/oms-monthly-summary';
import FormatAmountService from 'donor-elf-web/services/format-amount-service';
import moment from 'moment';
import Highcharts from 'highcharts';

export default class CustomReportsOmsMonthlySummaryController extends Controller {
  @service declare formatAmountService: FormatAmountService;

  model!: CustomReportOmsMonthlySummaryRouteModel;

  @action
  renderChart(el: any) {
    setTimeout(() => {
      Highcharts.chart(el, this.chartSettings);
    }, 50);
  }

  get descendingItems() {
    return sortByProperties(this.model.records, ['startDate:desc']);
  }

  @cached
  get ascendingItems() {
    return sortByProperties(this.model.records, ['startDate:asc']);
  }

  get revenueSeriesData() {
    return this.ascendingItems.map((item) => {
      return {
        y: item.totalRevenue,
        item,
      };
    });
  }

  get expenseSeriesData() {
    return this.ascendingItems.map((item) => {
      return {
        y: item.totalExpense * -1,
        item,
      };
    });
  }

  get balanceSeriesData() {
    return this.ascendingItems.map((item) => {
      return {
        y: item.closingBalance,
        item,
      };
    });
  }

  get categories() {
    return this.ascendingItems.map((x) =>
      moment(x.startDate).format('MMM YYYY'),
    );
  }

  get chartSettings() {
    let self = this;
    let minYAxis = Math.min(
      ...[
        0,
        ...this.model.records.map((x) => x.closingBalance),
        ...this.model.records.map((x) => x.totalRevenue),
        ...this.model.records.map((x) => x.totalExpense * -1),
      ],
    );

    let series = [
      {
        name: 'Revenue',
        color: '#5cb85c',
        data: this.revenueSeriesData,
      },
      {
        name: 'Expense',
        color: '#d9534f',
        data: this.expenseSeriesData,
      },
      {
        name: 'Balance',
        color: '#0b68a1',
        type: 'line',
        data: this.balanceSeriesData,
      },
    ];

    return {
      chart: {
        type: 'column',
        height: 300,
      },
      title: undefined,
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: this.categories,
        tickInterval: 3,
        tickLength: 0,
        labels: {
          autoRotation: false,
          align: 'left',
          y: 13,
        },
      },
      yAxis: {
        min: minYAxis,
        maxPadding: 0.0,
        endOnTick: false,
        title: {
          text: null,
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        formatter: function (): any {
          return `${this.x} ${this.series.name}: <b>${self.formatAmountService.format(this.y)}</b>`;
        },
      },
      plotOptions: {
        column: {
          groupPadding: 0.2,
          pointPadding: 0.01,
        },
      },
      series,
    };
  }
}
